import { useState, useMemo, useEffect } from "react";
import Table from "examples/Tables/Table";
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiBadge from "components/VuiBadge";
import VuiButton from "components/VuiButton";
import { FaPen, FaBan, FaTrash, FaRedo } from "react-icons/fa";
import AddUserDialog from "./AddUserDialog";
import EditUserDialog from "./EditUserDialog";
import StatusConfirmationDialog from "./StatusConfirmationDialog";
import ConfirmDeleteUserDialog from "./ConfirmDeleteUserDialog";
import useCommon from "hooks/useCommon";
import { updateUser } from "store/slice/userSlice";
import Snackbar from "components/Snackbar";
import { deleteUser } from "store/slice/userSlice";
import { getUsers } from "store/slice/userSlice";
import NoResults from "components/NoResult";
import { reserDeleteUserState } from "store/slice/userSlice";
import { updateUserStatus } from "store/slice/userSlice";
import VuiTooltip from "components/VuiTooltip";

import colors from "assets/theme/base/colors";
import rgba from "assets/theme/functions/rgba";
// import ViewUserDialog from "./ViewUserDialog";
// import EditUserDialog from "./EditUserDialog";

const { primary, dark } = colors;

/* ---------------- Cells ---------------- */

const NameCell = ({ name }) => (
  <VuiTypography variant="body2" fontWeight="regular">
    {name}
  </VuiTypography>
);

const EmailCell = ({ email }) => <VuiTypography variant="caption">{email}</VuiTypography>;

const RoleCell = ({ role }) => (
  <VuiBox
    px={1.5}
    py={0.5}
    border={`1px solid ${rgba(dark.main, 0.25)}`}
    borderRadius="12px"
    display="inline-flex"
    alignItems="center"
    justifyContent="center"
  >
    <VuiTypography variant="caption" fontWeight="medium">
      {role?.title || "—"}
    </VuiTypography>
  </VuiBox>
);

const StatusCell = ({ status, emailStatus }) => {
  const isActive = status === 1;
  const isEmailVerified = emailStatus;

  return (
    <VuiBadge
      variant="gradient"
      color={isActive && isEmailVerified ? "success" : "error"}
      size="sm"
      badgeContent={!isEmailVerified ? "Inactive" : isActive ? "Active" : "Deactivated"}
    />
  );
};

const LastLoginCell = ({ lastLogin }) => (
  <VuiTypography variant="caption">
    {lastLogin ? new Date(lastLogin).toLocaleDateString("en-US") : "Never"}
  </VuiTypography>
);

const ActionButtons = ({ user, onEdit, onToggleStatus, onDelete }) => {
  const isActive = user.status === 1;
  const emailVerified = user?.emailVerified;

  return (
    <VuiBox display="flex" justifyContent="center" gap={1}>
      <VuiTooltip title="Edit user">
        <VuiButton size="small" color="primary" variant="outlined" onClick={onEdit} sx={{ px: 1 }}>
          <FaPen size={12} />
        </VuiButton>
      </VuiTooltip>

      <VuiTooltip
        title={!emailVerified ? "User email is not verified" : isActive ? "Deactivate" : "Activate"}
      >
        <VuiButton
          size="small"
          variant="outlined"
          color={!emailVerified ? "error" : isActive ? "primary" : "success"}
          onClick={onToggleStatus}
          sx={{ px: 1 }}
          // disabled={!user?.emailVerified}
        >
          {!emailVerified ? (
            <FaBan size={12} />
          ) : isActive ? (
            <FaBan size={12} />
          ) : (
            <FaRedo size={12} />
          )}
        </VuiButton>
      </VuiTooltip>
      <VuiTooltip title="Delete user">
        <VuiButton size="small" variant="outlined" color="error" onClick={onDelete} sx={{ px: 1 }}>
          <FaTrash size={12} />
        </VuiButton>
      </VuiTooltip>
    </VuiBox>
  );
};
/* ---------------- Main Component ---------------- */

const UsersTable = ({ handleNotify, search }) => {
  const { DISPATCH, NAVIFATE, STORE } = useCommon();
  const { hotelIds } = STORE((state) => state.ui);
  const { list: users, listLoading, listError, updateSuccess } = STORE((state) => state.user);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const [editOpen, setEditOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null); // "activate" | "deactivate"
  const [targetUser, setTargetUser] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const filteredUsers = useMemo(() => {
    if (!search || search.trim().length < 3) return users;

    const q = search.toLowerCase();

    return users?.filter((user) => {
      return (
        user?.name?.toLowerCase().includes(q) ||
        user?.email?.toLowerCase().includes(q) ||
        user?.roleId?.title?.toLowerCase().includes(q)
      );
    });
  }, [users, search]);

  const handleCloseDelete = () => {
    setDeleteOpen(false);
    setTargetUser(null);
    DISPATCH(reserDeleteUserState());
  };

  const handleUpdateUser = async (userId, data) => {
    await DISPATCH(updateUser({ userId, data }))
      .unwrap()

      .then((res) => {
        const success = res?.success;

        if (success) {
          setEditOpen(false);

          handleNotify({
            color: success ? "success" : "error",
            title: success ? "User Updated" : "Error",
            content: res?.message || "User updated successfully",
            icon: success ? "check" : "warning",
            show: true,
          });
        }
      })
      .catch((err) => {
        setEditOpen(false);

        handleNotify({
          color: "error",
          title: "Failed to update user",
          content: err?.message || "Failed to update user",
          icon: "warning",
          show: true,
        });
      });
  };

  const handleStatusUpdate = async (userId, action) => {
    await DISPATCH(updateUserStatus({ userId, action }))
      .unwrap()
      .then((res) => {
        const success = res?.success;

        if (success) {
          setConfirmOpen(false);
          setTargetUser(null);
          setConfirmAction(null);
          handleNotify({
            color: success ? "success" : "error",
            title: success ? "User Updated" : "Error",
            content: "User status updated successfully",
            icon: success ? "check" : "warning",
            show: true,
          });
        }
      })
      .catch((err) => {
        setConfirmOpen(false);
        setTargetUser(null);
        setConfirmAction(null);
        handleNotify({
          color: "error",
          title: "Failed to update user",
          content: err?.message || "Failed to update user",
          icon: "warning",
          show: true,
        });
      });
  };

  const handledeleteConfirmation = async () => {
    const userId = targetUser?._id || null;
    await DISPATCH(deleteUser(userId))
      .unwrap()
      .then((res) => {
        const success = res?.apiRes?.success;

        if (success) {
          handleNotify({
            color: success ? "success" : "error",
            title: success ? "User Deleted" : "Error",
            content: res?.message || "User deleted successfully",
            icon: success ? "check" : "warning",
            show: true,
          });
          handleCloseDelete();
        }
      })
      .catch((err) => {
        handleNotify({
          color: "error",
          title: "Failed to delete user",
          content: err?.message || "Failed to delete user",
          icon: "warning",
          show: true,
        });
        handleCloseDelete();
      });
  };

  useEffect(() => {
    if (!Array.isArray(hotelIds) || hotelIds.length === 0) return;
    DISPATCH(getUsers({ hotelId: hotelIds, type: "admin" }));
  }, [hotelIds, DISPATCH]);

  const tableData = useMemo(() => {
    return {
      columns: [
        { name: "name", align: "left" },
        { name: "email", align: "left" },
        { name: "role", align: "center" },
        { name: "status", align: "center" },
        { name: "last login", align: "center" },
        { name: "actions", align: "center" },
      ],

      rows: filteredUsers?.map((user) => ({
        name: <NameCell name={user.name} />,

        email: <EmailCell email={user.email} />,

        role: <RoleCell role={user.roleId} />,

        status: <StatusCell status={user.status} emailStatus={user.emailVerified} />,

        "last login": <LastLoginCell lastLogin={user.lastLogin} />,

        actions: (
          <ActionButtons
            user={user}
            onEdit={() => {
              setSelectedUser(user);
              setEditOpen(true);
            }}
            onToggleStatus={() => {
              if (!user?.emailVerified) {
                return;
              }
              setTargetUser(user);
              setConfirmAction(user.status === 1 ? "deactivate" : "activate");
              setConfirmOpen(true);
            }}
            onDelete={() => {
              setTargetUser(user);
              setDeleteOpen(true);
            }}
          />
        ),
      })),
    };
  }, [filteredUsers, users]);

  const isSearching = search && search.trim().length >= 3;
  const isSearchEmpty = isSearching && filteredUsers?.length === 0;
  const isInitialEmpty = !isSearching && filteredUsers?.length === 0;

  return (
    <>
      {isSearchEmpty ? (
        <NoResults title="No User found" search={search} />
      ) : tableData ? (
        <Table
          columns={tableData.columns}
          rows={tableData.rows}
          loading={listLoading}
          pagination
          paginationMode="client"
          page={page}
          rowsPerPage={rowsPerPage}
          total={tableData?.rows?.length}
          onPageChange={(newPage) => setPage(newPage)}
          onRowsPerPageChange={(newLimit) => {
            setRowsPerPage(newLimit);
            setPage(0);
          }}
          emptyState={isInitialEmpty}
          emptyTitle="No users created yet"
        />
      ) : (
        <></>
      )}

      {editOpen && (
        <EditUserDialog
          open={editOpen}
          user={selectedUser}
          onClose={() => {
            setEditOpen(false);
            setSelectedUser(null);
          }}
          onSave={(userId, payload) => {
            handleUpdateUser(userId, payload);
          }}
        />
      )}

      {confirmOpen && (
        <StatusConfirmationDialog
          open={confirmOpen}
          action={confirmAction}
          userName={targetUser?.name}
          onClose={() => {
            setConfirmOpen(false);
            setTargetUser(null);
            setConfirmAction(null);
          }}
          onConfirm={() => {
            handleStatusUpdate(targetUser._id, confirmAction);
          }}
        />
      )}

      {deleteOpen && (
        <ConfirmDeleteUserDialog
          open={deleteOpen}
          userName={targetUser?.name}
          onClose={handleCloseDelete}
          onConfirm={handledeleteConfirmation}
        />
      )}
    </>
  );
};

export default UsersTable;
